'use client';

import React, { PropsWithChildren, useTransition } from 'react';
import { StoreCodeNameAttribute } from '@/src/lib/store/types';
import Select from '@/src/app/components/Select/Select';
import { usePathname, useRouter } from 'next/navigation';
import { useFormHelpers } from '@/src/lib/utils/form-helpers';
import { Dictionary } from '@/src/lib/dictionary/types';
import { changeCurrency } from '@/src/lib/currency/business/change-currency';
import { isFailure } from '@/src/lib/http/business/server-action-results';
import { eventBus } from '@/src/lib/core/event/bus.ts';
import { EventBusName } from '@/src/lib/core/event/types.ts';
import { useAsyncStartTransition } from '@/src/lib/utils/hooks/use-async-start-transition.ts';

export interface CurrencyDropdownProps {
  currencies: Array<StoreCodeNameAttribute>;
  defaultCurrency: string;
  dict: Dictionary;
  lang: string;
  onCurrencyChange: typeof changeCurrency;
}

export function CurrencyDropdown({
  currencies,
  defaultCurrency,
  dict,
  lang,
  onCurrencyChange,
}: PropsWithChildren<CurrencyDropdownProps>) {
  const pathname = usePathname();
  const router = useRouter();

  const options = currencies.map(({ code, name }) => ({
    value: code,
    label: `${code} (${name})`,
  }));

  const [isPending] = useTransition();
  const asyncStartTransition = useAsyncStartTransition();
  const { handleErrors } = useFormHelpers(dict);

  async function onSelectionChangeHook({ value }: { value: string | number }) {
    const currency = String(value);

    if (currency === defaultCurrency) return;

    await asyncStartTransition(async () => {
      try {
        const response = await onCurrencyChange(currency, lang);

        if (isFailure(response)) {
          throw new Error(dict.general.error);
        }
      } catch (e) {
        handleErrors(e);
      }
    });
    router.refresh();
    eventBus.emit(EventBusName.CURRENCY_CHANGED);
  }

  if (pathname && pathname.indexOf('/checkout') > -1 && pathname.indexOf('/checkout/success') === -1) return null;

  return (
    <Select
      options={options}
      value={defaultCurrency}
      onSelectionChangeHook={onSelectionChangeHook}
      placeholder={dict.general.selectFieldPlaceholder}
      label={dict.general.currency}
      name="currency-toggle"
      className="min-w-[16rem]"
      disabled={currencies.length <= 1 || isPending}
      testId="currency-dropdown"
    />
  );
}

export default CurrencyDropdown;
